/**
 * C-GTCC-VIEW-OVW1 — GTCC view "Risk Radar", 1:1 after
 * `GTCC_REFERENCE_2026-09-01.html`, `div[data-v="risk"]` (lines 606-628).
 *
 * Layout of the reference, element for element:
 *   - head `h1 Risk Radar` with the freshness badge and the `.sub` line
 *     "Je Tenant: Wo brennt es zuerst beim Kunden?",
 *   - panel "Tenant-Risiko": the table TENANT · FAHRZEUGE · STILL > 24 H ·
 *     ALARME 7 T · DATA CONF. · RISIKO, one row per tenant,
 *   - `.grid-main`: panel `Warum „…"? (<TENANT>)` for the leading tenant and
 *     panel "Radar-Logik" with the muted mono sentence.
 *
 * P17: this view does not aggregate. Every row is one tenant's own
 * measurement; the only cross-tenant operation is the ordering.
 *
 * Honesty (Owner P8/P9/P10): the counts come from `ops/vehicle-flow`, the
 * explanations from the existing risk-radar module. A missing count renders
 * "nicht messbar" and takes the tenant's band to `unmeasurable` — never to
 * `low`, which would read as "measured and fine". Unmeasurable tenants sort to
 * the end rather than to either edge of a ranking they were not part of.
 *
 * One deviation from the reference remains deliberate, quantitatively
 * derived in `docs/design/gtcc-screens/D-GTCC-REGRESS1/ABGLEICH.md`
 * (D-GTCC-FIX-RISK-AIA1): the row order follows the formula's own risk score
 * descending, while the reference sheet's fixed order matches fleet size
 * descending instead — re-deriving the reference's five scores from its own
 * displayed values shows fleet size, not risk, as the reference's sort key.
 * Ordering by risk is this panel's stated purpose ("wo brennt es zuerst"), so
 * the reference's order is not the correction target here.
 *
 * The `Karte →` link of the "Warum" panel now targets Tenant 360° (view `ten`,
 * tab `t360`) via `goToTenant360`, the closest existing live route to a
 * per-tenant map/detail view; no dedicated fleet-map view exists in this
 * console. The extra "4 Alarme in den letzten 7 Tagen" line the reference
 * omits is a genuine measured signal from `/v1/admin/ai/risk-radar`
 * (`fixtures/v1__admin__ai__risk-radar.json`), not a hardcoded addition —
 * kept for the same honesty reason as every other measured row here.
 */
import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useState, type CSSProperties, type ReactNode } from "react";

import type { GtccResult } from "../../api/gtcc-result.js";
import {
  OPS_VEHICLE_FLOW_RISK_KEY,
  fetchOpsVehicleFlow,
  type OpsTenantFlowDto,
  type OpsVehicleFlowDto,
} from "../../api/ops.js";
import {
  RISK_TENANTS_KEY,
  fetchRiskRadar,
  type RiskLevel,
  type RiskRadarDto,
  type RiskTenantDto,
} from "../../api/risk.js";
import { writeGtccViewKeyToLocation } from "../../deep-link-url.js";
import { Dot, HostCell } from "../../ui/primitives.js";
import {
  ControlFreshBadge,
  ControlHead,
  ControlMono,
  ControlNotMeasurable,
  ControlNote,
  ControlPanel,
  ControlRow,
  ControlTableWrap,
  GRID_COLUMN_STYLE,
  GRID_MAIN_CLASS,
  formatInteger,
  formatWholePercent,
  measuredNumber,
  toneColor,
  type ControlTone,
} from "../control/ControlPrimitives.js";
import {
  compareByRiskDesc,
  computeRiskScore,
  isSilentShareWarn,
  type RiskBand,
} from "./risk-score.js";

/* ---------------------------------------------------------------- queries */

const RISK_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

function dataOf<T>(query: UseQueryResult<GtccResult<T>>): T | null {
  return query.data !== undefined && query.data.ok ? query.data.data : null;
}

/* ------------------------------------------------------------- url state */

/**
 * The sorted column lives in the URL so that a shared Risk Radar link opens on
 * the column the sender was looking at. Same mechanism as the cockpit tabs:
 * `history.replaceState`, so sorting does not grow the back stack.
 */
export const RISK_SORT_PARAM = "risk_sort";
export const RISK_DIR_PARAM = "risk_dir";

export const RISK_COLUMNS = [
  "tenant",
  "vehicles",
  "silent",
  "alerts",
  "confidence",
  "risk",
] as const;
export type RiskColumn = (typeof RISK_COLUMNS)[number];

export type RiskDirection = "asc" | "desc";

/** The reference's own order: the riskiest tenant on top. */
export const RISK_SORT_DEFAULT: RiskColumn = "risk";
export const RISK_DIR_DEFAULT: RiskDirection = "desc";

function readQueryParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

function writeQueryParam(name: string, value: string | null): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (value === null) url.searchParams.delete(name);
  else url.searchParams.set(name, value);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function initialColumn(): RiskColumn {
  const raw = readQueryParam(RISK_SORT_PARAM);
  return RISK_COLUMNS.find((column) => column === raw) ?? RISK_SORT_DEFAULT;
}

function initialDirection(): RiskDirection {
  const raw = readQueryParam(RISK_DIR_PARAM);
  return raw === "asc" || raw === "desc" ? raw : RISK_DIR_DEFAULT;
}

/** Text columns read best ascending, measurements worst-first. */
function naturalDirection(column: RiskColumn): RiskDirection {
  return column === "tenant" ? "asc" : "desc";
}

/* ----------------------------------------------------------------- rows */

interface RiskRow {
  readonly tenantId: string;
  readonly name: string;
  readonly vehicles: number | null;
  readonly silentOver24h: number | null;
  readonly alerts7d: number | null;
  readonly dataConfidencePercent: number | null;
  readonly score: number | null;
  readonly band: RiskBand;
  readonly signals: ReadonlyArray<{ readonly status: RiskLevel; readonly explanation: string }>;
}

function buildRows(
  flow: OpsVehicleFlowDto | null,
  radar: RiskRadarDto | null,
): ReadonlyArray<RiskRow> {
  const byId = new Map<string, RiskTenantDto>(
    (Array.isArray(radar?.radar?.tenants) ? radar.radar.tenants : []).map((tenant) => [
      tenant.tenantId,
      tenant,
    ]),
  );
  return (Array.isArray(flow?.tenants) ? flow.tenants : []).map((tenant: OpsTenantFlowDto) => {
    const scored = computeRiskScore(tenant);
    return {
      tenantId: tenant.tenantId,
      name: tenant.name,
      vehicles: measuredNumber(tenant.vehicles),
      silentOver24h: measuredNumber(tenant.silentOver24h),
      alerts7d: measuredNumber(tenant.alerts7d),
      dataConfidencePercent: measuredNumber(tenant.dataConfidencePercent),
      score: scored.score,
      band: scored.band,
      signals: (() => {
        const signals = byId.get(tenant.tenantId)?.signals;
        return Array.isArray(signals) ? signals : [];
      })(),
    };
  });
}

/** A `null` measurement never wins a comparison; it sorts to the end. */
function compareNullable(a: number | null, b: number | null, direction: RiskDirection): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return direction === "asc" ? a - b : b - a;
}

export function sortRows(
  rows: ReadonlyArray<RiskRow>,
  column: RiskColumn,
  direction: RiskDirection,
): ReadonlyArray<RiskRow> {
  const sorted = [...rows];
  if (column === "risk" && direction === "desc") {
    /* The formula's own total order, so table and short form agree. */
    return sorted.sort(compareByRiskDesc);
  }
  sorted.sort((a, b) => {
    const primary =
      column === "tenant"
        ? direction === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
        : column === "vehicles"
          ? compareNullable(a.vehicles, b.vehicles, direction)
          : column === "silent"
            ? compareNullable(a.silentOver24h, b.silentOver24h, direction)
            : column === "alerts"
              ? compareNullable(a.alerts7d, b.alerts7d, direction)
              : column === "confidence"
                ? compareNullable(a.dataConfidencePercent, b.dataConfidencePercent, direction)
                : compareNullable(a.score, b.score, direction);
    return primary !== 0 ? primary : a.name.localeCompare(b.name);
  });
  return sorted;
}

const BAND_TONE: Readonly<Record<RiskBand, ControlTone>> = {
  elevated: "warn",
  watch: "warn",
  low: "ok",
  unmeasurable: "muted",
};

/* ------------------------------------------------------------------- view */

export function RiskView(): ReactNode {
  const { t, locale } = useLocale();
  const [column, setColumn] = useState<RiskColumn>(initialColumn);
  const [direction, setDirection] = useState<RiskDirection>(initialDirection);

  const flowQuery = useQuery({
    queryKey: OPS_VEHICLE_FLOW_RISK_KEY,
    queryFn: ({ signal }) => fetchOpsVehicleFlow(signal),
    ...RISK_QUERY_OPTIONS,
  });
  const radarQuery = useQuery({
    queryKey: RISK_TENANTS_KEY,
    queryFn: ({ signal }) => fetchRiskRadar(signal),
    ...RISK_QUERY_OPTIONS,
  });

  const flow = dataOf(flowQuery);
  const radar = dataOf(radarQuery);

  /* Clicking the active column flips it, clicking another opens it in its
     natural direction. Both halves go into the URL immediately. */
  const onSort = useCallback(
    (next: RiskColumn) => {
      const nextDirection: RiskDirection =
        column === next ? (direction === "asc" ? "desc" : "asc") : naturalDirection(next);
      setColumn(next);
      setDirection(nextDirection);
      writeQueryParam(RISK_SORT_PARAM, next);
      writeQueryParam(RISK_DIR_PARAM, nextDirection);
    },
    [column, direction],
  );

  const headFreshness = measuredNumber(flow?.freshnessSeconds);
  const head = (
    <ControlHead
      testId="gtcc-risk-head"
      title={t("admin.gtcc.risk.head.title")}
      subtitle={t("admin.gtcc.risk.head.subtitle")}
      fresh={
        <ControlFreshBadge
          testId="gtcc-risk-fresh"
          stale={flow !== null && (flow.status === "stale" || flow.status === "unmeasurable")}
          text={
            headFreshness === null
              ? t("admin.gtcc.risk.fresh.unmeasurable")
              : t("admin.gtcc.risk.fresh.seconds", {
                  seconds: formatInteger(headFreshness, locale),
                })
          }
        />
      }
    />
  );

  if (flowQuery.isPending && radarQuery.isPending) {
    return (
      <Stack gap={3} data-testid="gtcc-view-risk">
        {head}
        <div data-testid="gtcc-risk-loading">
          <LoadingState label={t("admin.gtcc.risk.loading")} />
        </div>
      </Stack>
    );
  }

  /*
   * A 403 is not an unmeasured value: the operator hears "not allowed", never
   * "could not be measured" (Owner P9), as everywhere else in the deck.
   */
  const deniedOnly = [flowQuery, radarQuery].some(
    (query) => query.data !== undefined && !query.data.ok && query.data.kind === "forbidden",
  );

  if (flow === null && radar === null && deniedOnly) {
    return (
      <Stack gap={3} data-testid="gtcc-view-risk">
        {head}
        <div data-testid="gtcc-risk-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.risk.forbidden")}
            body={t("admin.gtcc.risk.forbidden.body")}
          />
        </div>
      </Stack>
    );
  }

  if (flow === null && radar === null) {
    return (
      <Stack gap={3} data-testid="gtcc-view-risk">
        {head}
        <div data-testid="gtcc-risk-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.risk.failure.title")}
            body={t("admin.gtcc.risk.failure.body")}
          />
        </div>
      </Stack>
    );
  }

  const rows = sortRows(buildRows(flow, radar), column, direction);
  /* The "Warum" panel always explains the riskiest tenant, whatever the table
     is sorted by — it answers "who first?", not "what did I click?". */
  const leader = [...rows].sort(compareByRiskDesc)[0];

  return (
    <Stack gap={3} data-testid="gtcc-view-risk">
      {head}
      <ControlPanel testId="gtcc-risk-table" title={t("admin.gtcc.risk.table.title")}>
        {rows.length === 0 ? (
          <ControlNotMeasurable
            testId="gtcc-risk-table-empty"
            label={t("admin.gtcc.risk.table.empty")}
          />
        ) : (
          <ControlTableWrap>
            {/* aria-label statt `caption`: die Referenz zeigt keine Bildunterschrift,
                aber eine Tabelle ohne Namen bleibt im Screenreader namenlos.
                Der Name ist der Titel des Panels darueber. */}
            <table aria-label={t("admin.gtcc.risk.table.title")}>
              <thead>
                <tr>
                  {RISK_COLUMNS.map((key) => (
                    <SortableHeader
                      key={key}
                      column={key}
                      active={column === key}
                      direction={direction}
                      onSort={onSort}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <RiskTableRow key={row.tenantId} row={row} />
                ))}
              </tbody>
            </table>
          </ControlTableWrap>
        )}
      </ControlPanel>

      <div className={GRID_MAIN_CLASS}>
        <div style={GRID_COLUMN_STYLE}>
          <WhyPanel row={leader} />
        </div>
        <div style={GRID_COLUMN_STYLE}>
          <ControlPanel testId="gtcc-risk-logic" title={t("admin.gtcc.risk.logic.title")}>
            <ControlNote testId="gtcc-risk-logic-text">
              {t("admin.gtcc.risk.logic.text")}
            </ControlNote>
          </ControlPanel>
        </div>
      </div>
    </Stack>
  );
}

/* ---------------------------------------------------------------- header */

const COLUMN_LABEL_KEY: Readonly<Record<RiskColumn, string>> = {
  tenant: "admin.gtcc.risk.col.tenant",
  vehicles: "admin.gtcc.risk.col.vehicles",
  silent: "admin.gtcc.risk.col.silent",
  alerts: "admin.gtcc.risk.col.alerts",
  confidence: "admin.gtcc.risk.col.confidence",
  risk: "admin.gtcc.risk.col.risk",
};

function SortableHeader(props: {
  column: RiskColumn;
  active: boolean;
  direction: RiskDirection;
  onSort: (column: RiskColumn) => void;
}): ReactNode {
  const { t } = useLocale();
  const label = t(COLUMN_LABEL_KEY[props.column]);
  return (
    <th
      scope="col"
      data-testid={`gtcc-risk-col-${props.column}`}
      aria-sort={props.active ? (props.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => props.onSort(props.column)}
        aria-label={t("admin.gtcc.risk.sort.by", { column: label })}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          letterSpacing: "inherit",
        }}
      >
        {label}
      </button>
    </th>
  );
}

/* ------------------------------------------------------------------ rows */

function RiskTableRow(props: { row: RiskRow }): ReactNode {
  const { t, locale } = useLocale();
  const { row } = props;
  const notMeasurable = <ControlNotMeasurable label={t("admin.gtcc.risk.not_measurable")} />;
  const silentWarn = isSilentShareWarn(row.vehicles, row.silentOver24h);
  const tone = BAND_TONE[row.band];

  return (
    <tr data-testid={`gtcc-risk-row-${row.tenantId}`} data-band={row.band}>
      <HostCell name={row.name} />
      <td>
        {row.vehicles === null ? (
          notMeasurable
        ) : (
          <ControlMono>{formatInteger(row.vehicles, locale)}</ControlMono>
        )}
      </td>
      <td data-warn={silentWarn ? "true" : "false"}>
        {row.silentOver24h === null ? (
          notMeasurable
        ) : (
          <ControlMono tone={silentWarn ? "warn" : undefined}>
            {formatInteger(row.silentOver24h, locale)}
          </ControlMono>
        )}
      </td>
      <td>
        {row.alerts7d === null ? (
          notMeasurable
        ) : (
          <ControlMono>{formatInteger(row.alerts7d, locale)}</ControlMono>
        )}
      </td>
      <td>
        {row.dataConfidencePercent === null ? (
          notMeasurable
        ) : (
          <ControlMono>{formatWholePercent(row.dataConfidencePercent, locale)}</ControlMono>
        )}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        <Dot tone={tone} />
        <span style={{ marginInlineStart: "var(--gt-sys-space-2)", color: toneColor(tone) }}>
          {t(`admin.gtcc.risk.band.${row.band}`)}
        </span>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------ why panel */

const RISK_LEVEL_TONE: Readonly<Record<RiskLevel, ControlTone>> = {
  ok: "ok",
  watch: "warn",
  warning: "warn",
  critical: "crit",
  unknown: "muted",
};

/*
 * The reference's `Karte →` cell: accent-coloured mono link at the end of a
 * row. Matches `ACCENT_STYLE`/`OPEN_BUTTON_STYLE` in `TenPanels.tsx` /
 * `TenantsTab.tsx` pixel-for-pixel; duplicated locally rather than imported
 * across view folders, the pattern this file already follows for
 * `ControlRow`/`ControlPanel` etc.
 */
const WHY_LINK_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-accent)",
  appearance: "none",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
};

/**
 * Opens Tenant 360° for `tenantId` — the reference's `Karte →` link has no
 * fleet-map counterpart in this console, so this is the nearest live
 * per-tenant route. Mirrors `goTo()` in `OvwView.tsx`.
 */
function goToTenant360(tenantId: string): void {
  writeGtccViewKeyToLocation("ten", false, import.meta.env.BASE_URL);
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "t360");
    url.searchParams.set("tenant", tenantId);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}

function WhyPanel(props: { row: RiskRow | undefined }): ReactNode {
  const { t } = useLocale();
  const { row } = props;

  if (row === undefined) {
    return (
      <ControlPanel testId="gtcc-risk-why" title={t("admin.gtcc.risk.why.title_empty")}>
        <ControlNotMeasurable
          testId="gtcc-risk-why-empty"
          label={t("admin.gtcc.risk.table.empty")}
        />
      </ControlPanel>
    );
  }

  return (
    <ControlPanel
      testId="gtcc-risk-why"
      title={t("admin.gtcc.risk.why.title", {
        band: t(`admin.gtcc.risk.band.${row.band}`),
        tenant: row.name,
      })}
    >
      <Stack gap={0}>
        {row.signals.length === 0 ? (
          <ControlNotMeasurable
            testId="gtcc-risk-why-no-signals"
            label={t("admin.gtcc.risk.why.no_signals")}
          />
        ) : (
          row.signals.map((signal, index) => (
            <ControlRow
              key={`${signal.status}-${index}`}
              testId={`gtcc-risk-why-signal-${index}`}
              tone={RISK_LEVEL_TONE[signal.status]}
              label={signal.explanation}
              value={
                index === 0 ? (
                  <button
                    type="button"
                    style={WHY_LINK_STYLE}
                    data-testid="gtcc-risk-why-open-360"
                    onClick={() => goToTenant360(row.tenantId)}
                  >
                    {t("admin.gtcc.ten.open_360")}
                  </button>
                ) : undefined
              }
            />
          ))
        )}
        {/*
         * The reference closes the panel with the advice row (line 620) and
         * the instruction lists it as an `info` row. It is advice, not a
         * measurement: it carries no number and is therefore printed for the
         * leading tenant whatever the module measured — including the case
         * where no signal came back at all.
         */}
        <ControlRow
          testId="gtcc-risk-why-recommendation"
          tone="info"
          label={t("admin.gtcc.risk.why.recommendation")}
        />
      </Stack>
    </ControlPanel>
  );
}
